import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Button,
  Progress,
  Spinner,
  Typography,
} from "@material-tailwind/react";
import { useTranslation } from "react-i18next";

import { api_key, sdkURL, user_id } from "../utils/api-url-list";
import faceScan from "../utils/faceScan";

import Failure from "../assets/images/failure.gif";
import { ReactComponent as CheckFilled } from "../assets/icons/CheckFilled.svg";

const Scan = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation("Scan");
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingStep, setAnalyzingStep] = useState(0);
  const [error, setError] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "",
    timeElapsed: 0,
    isLightMode: false,
    fps: 0,
  });

  const analyzingMsgDuration = 1000;
  const analyzingMsgList = useMemo(
    () => [
      t("analyzing_message_0"),
      t("analyzing_message_1"),
      t("analyzing_message_2"),
      t("analyzing_message_3"),
      t("analyzing_message_4"),
      t("analyzing_message_5"),
    ],
    [t]
  );

  useEffect(() => {
    let timer;
    if (analyzing) {
      if (analyzingStep < analyzingMsgList.length - 1) {
        timer = setTimeout(() => {
          setAnalyzingStep((s) => s + 1);
        }, analyzingMsgDuration);
      }
    } else setAnalyzingStep(0);
    return () => {
      clearTimeout(timer);
    };
  }, [analyzing, analyzingMsgList.length, analyzingStep]);

  useEffect(() => {
    if (state.token?.length > 0) {
      faceScan.onFrame((fd) => {
        const { landmarksList, tone, ...framedata } = fd;
        setScanFrameData(framedata);
      });
      faceScan.onScanFinish(({ raw_intensity, ppg_time, average_fps }) => {
        setAnalyzing(true);
        Promise.all([
          new Promise(async (resolve, reject) => {
            try {
              const resp = await fetch(sdkURL("/vitals/add-scan"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  employee_id: user_id,
                  api_key,
                  scan_token: state.token,
                  posture: "Resting",
                  dob: state.dob,
                  gender: state.gender,
                  metadata: {
                    physiological_scores: {
                      height: state.height,
                      weight: state.weight,
                    },
                    device: `RPPG_CAREPLIX_FACE_${
                      navigator.platform.match(/iPhone|iPod|iPad/)
                        ? "IOS"
                        : "ANDROID"
                    }`,
                    ppg_time: ppg_time,
                    raw_intensity: raw_intensity,
                    fps: average_fps,
                  },
                }),
              });
              const resp_json = await resp.json();
              if (resp_json.statusCode?.toString().startsWith("2"))
                resolve(resp_json);
              else throw new Error(resp_json.message);
            } catch (err) {
              reject(err);
            }
          }),
          new Promise((resolve) => {
            setTimeout(resolve, analyzingMsgList.length * analyzingMsgDuration);
          }),
        ])
          .then(([result]) => {
            navigate("/result", { state: { result }, replace: true });
          })
          .catch((err) => {
            console.error(err);
            setError(err.message);
          });
      });
      faceScan.onError((err) => {
        console.error(err);
        setError(err.message);
      });
      faceScan
        .startScan(60000, 60000, "/model")
        .then((resolution) => {
          console.log("Scan Started");
        })
        .catch((err) => {
          console.error(err);
        });
    } else setError("Invalid Scan Token");
    return () => {
      faceScan.stopScan(true);
    };
  }, [navigate, state, analyzingMsgList.length]);

  return (
    <section className="relative shrink-0 grow px-[10%]">
      <div className="relative h-[65vh] overflow-hidden">
        <video
          className="absolute bottom-12 left-8 w-px h-px bg-black"
          id="videoInput"
          autoPlay
          muted
          playsInline
        />
        <canvas
          id="canvasOutput"
          className="absolute h-full inset-y-0 left-1/2 -translate-x-1/2 -scale-x-100"
        />
        {!faceScan.isFaceInView() && (
          <p className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl bg-black/50 text-white text-xl text-center font-medium">
            {t("noFace")}
          </p>
        )}
        {faceScan.isInitializing() && (
          <div className="absolute inset-0 bg-[#fcfcfc] px-8 py-12 flex flex-col items-center justify-center text-center">
            <Spinner color="red" className="h-6 w-6 text-primary/20" />
            <Typography variant="h6" className="mt-4 text-primary font-medium">
              {t("initializing")}
            </Typography>
          </div>
        )}
      </div>
      <div className="my-8">
        {scanFrameData.type === "scan" && (
          <Progress
            value={(scanFrameData.timeElapsed - 10000) / 600}
            color="red"
            size="sm"
            className="mb-3"
            barProps={{ className: "bg-primary text-white" }}
          />
        )}
        <Typography variant="h6" className="text-primary text-center">
          {scanFrameData.type === "scan"
            ? `${Math.round((scanFrameData.timeElapsed - 10000) / 600)}% ${t(
                "completed"
              )}`
            : `${Math.ceil((10000 - scanFrameData.timeElapsed) / 1000)} ${t(
                "seconds"
              )} ${t("start")}`}
        </Typography>
      </div>
      <Button
        color="white"
        className="mb-4 w-full bg-white text-primary/80 border border-stroke text-sm font-medium normal-case"
        onClick={() => navigate(-1)}
      >
        {t("Cancel")}
      </Button>
      {error.length > 0 ? (
        <div className="absolute inset-0 bg-[#fcfcfc] px-8 py-12 flex flex-col items-center justify-start text-center">
          <img src={Failure} alt="failure icon" className="w-1/3 mx-auto" />
          <Typography
            variant="lead"
            className="mt-4 text-primary whitespace-pre-line"
          >
            {"Scan Failed!\nPlease try again."}
          </Typography>
          <Button
            color="white"
            className="mt-6 bg-white text-primary/80 border border-stroke text-sm normal-case"
            onClick={() => navigate(-1)}
          >
            {t("Back")}
          </Button>
        </div>
      ) : (
        analyzing && (
          <div className="absolute inset-0 bg-[#fcfcfc] p-[15%] pt-[5%] flex flex-col items-stretch justify-evenly gap-6">
            {analyzingMsgList.map((msg, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-6"
              >
                <Typography
                  variant="h6"
                  className="text-primary tracking-widest"
                >
                  {msg}
                </Typography>
                {analyzingStep === index ? (
                  <Spinner
                    color="red"
                    className="shrink-0 h-12 w-12 text-primary/20"
                  />
                ) : (
                  analyzingStep > index && (
                    <CheckFilled className="shrink-0 h-12 w-12 text-secondary" />
                  )
                )}
              </div>
            ))}
          </div>
        )
      )}
    </section>
  );
};

export default Scan;
